/* eslint-disable no-undefined */
import { Injectable } from "@angular/core";
import { StorageService } from "./storage.service";
import { environment } from "../../environments/environment";
import {USER_TYPE, ROUTES, ROUTE_PREFIX, API_ENDPOINTS} from "../constants";
import { HttpClient } from "@angular/common/http";
import { BehaviorSubject, Observable } from "rxjs";
import { CookieService } from "ngx-cookie-service";
import { NgxSpinnerService } from "ngx-spinner";
import { UtilityService } from "./utility.service";
import { IOrganization } from "../dtos";
import { NotificationService } from "./notification.service";
import { UserStatusesEnum } from "./crud-table/models/users.model"; import {I18nService} from "./i18n.service";
import {SessionService} from "./session.service";
import {PublicHttpService} from "./publicHttp.service";

export interface INotificationSettingsObject {
	email: {
		newOrder: boolean,
		newTender: boolean,
		newsletter: boolean,
		newContract: boolean,
		newQuotation: boolean,
		updatedTender: boolean,
		productsRequest: boolean,
		newComment: boolean
	}
}

export const EMPTY_USER_NOTIFICATIONS = {
	email: {
		newOrder: true,
		newTender: true,
		newsletter: true,
		newContract: true,
		newQuotation: true,
		updatedTender: true,
		productsRequest: true,
		newComment: true
	}
};

const EMPTY_ORGANIZATION = {
	id: undefined,
	name: "",
	type: "",
	status: "",
	commercialRegisterNumber:"",
	website: "",
	email: "",
	description: "",
	parentOrganizationId: "",
	categories: [],
	initialCategorySelected: false,
	vat: "",
	primaryContact: "",
	termsAndConditions: "",
	paymentTerms:"",
	deliveryTerms:""
};

export interface IUserProfile {
	id: string,
	firstName?: string,
	lastName?: string,
	email: string,
	phone: string,
	lasLogin: Date,
	lastSelectedOrganizationId: string,
	showWelcomeScreen: boolean,
	status: UserStatusesEnum,
	addressIds: string,
	organizations: IOrganization[],
	preferredLanguage: string,
	notificationSettingsObject: INotificationSettingsObject,
}

const EMPTY_USER_PROFILE = {
	id: undefined,
	firstname: "",
	lastname: "",
	email: "",
	phone: "",
	lasLogin: new Date,
	lastSelectedOrganizationId: "",
	showWelcomeScreen: false,
	addressIds: "",
	organizations: [ EMPTY_ORGANIZATION ],
	status: UserStatusesEnum.inactive,
	preferredLanguage: '',
	notificationSettingsObject: EMPTY_USER_NOTIFICATIONS
};

@Injectable( {
	providedIn: "root"
} )
export class ProfileService {
	private _userProfile$ = new BehaviorSubject<IUserProfile>( EMPTY_USER_PROFILE );
	private _profileName$ = new BehaviorSubject<string>( "" );
	private _profileId$ = new BehaviorSubject<string>( "" );
	private _lastSelectedOrganization$ = new BehaviorSubject<IOrganization>( EMPTY_ORGANIZATION );
	private _userRoutes;
	private _baseURL: string = "";
	private _profileId: string = "";
	public currentOrg: string;
	public _userOrganization;

	constructor(
		private storageService: StorageService,
		private httpClient: HttpClient,
		private publicHttpService: PublicHttpService,
		private cookieService: CookieService,
		private utilityService: UtilityService,
		private spinnerService: NgxSpinnerService,
		private notificationService: NotificationService,
		private i18nService: I18nService,
		private sessionService: SessionService

	) {
		this.storageService = storageService;
		this.httpClient = httpClient;
		this.cookieService = cookieService;
		this.utilityService = utilityService;
		this.spinnerService = spinnerService;
		this.notificationService = notificationService;
		this.i18nService = i18nService;

		if( this.profileType === USER_TYPE.BUYER ){
			this._userRoutes = environment.API_ROUTES.BUYER;
		} else if( this.profileType === USER_TYPE.SUPPLIER ) {
			this._userRoutes = environment.API_ROUTES.SUPPLIER;
		}

		if( this.profileType ){
			this._baseURL = environment.API_ENDPOINT + environment.VERSION_V1 + this._userRoutes.PROFILE;
		}
	}

	get userProfile$() {
		return this._userProfile$.asObservable();
	}

	get userProfile() {
		return this._userProfile$.value;
	}

	set userRoutes( routes: object ){
		this._userRoutes = routes;
	}

	get lastSelectedOrganization$() {
		return this._lastSelectedOrganization$.asObservable();
	}

	get profileId() {
		return this._profileId;
	}

	get profileId$(){
		return this._profileId$.asObservable();
	}

	get userRoutes() {
		return this._userRoutes;
	}

	get currentOrganization() {
		return this._userOrganization;
	}

	get profileType() {
		return this.sessionService.userType;
	}

	private _userApproved$: BehaviorSubject<boolean> = new BehaviorSubject(false);

	get isUserApproved$() {
		return this._userApproved$.asObservable();
	}

	setIsUserApproved() {
		this.userProfile$.subscribe(response => {
			const index = response.organizations.findIndex((org) => org.id === response.lastSelectedOrganizationId);
			this._userApproved$.next(response.organizations[index]['approved']);
		});
	}

	/**
	 * Loads the profile of the current user from backend
	 */
	loadProfile() {
		const URI = this.profileType === USER_TYPE.BUYER ? environment.API_ROUTES.BUYER.PROFILE.GET : environment.API_ROUTES.SUPPLIER.PROFILE.GET;

		this.spinnerService.show();
		this.httpClient.get( environment.API_ENDPOINT + environment.VERSION_V1 + URI ).subscribe( ( profile: IUserProfile ) => {
			this._profileId$.next( profile.id );
			this._profileId = profile.id;
			this.currentOrg = this._userOrganization;
			profile.organizations.map( ( org: IOrganization ) => {
				if ( org.id === profile.lastSelectedOrganizationId ) {
					this._userOrganization = org;
					this._lastSelectedOrganization$.next( org );
					profile.notificationSettingsObject = org.organizationSettings.notificationSettingsObject;
					profile.status = org.organizationSettings.status;
				}
			} );
			this._userProfile$.next( profile );
			this.setProfileName( profile );
			if (this.i18nService.currentLanguage.value !== profile.preferredLanguage) {
				this.i18nService.switchLanguage(profile.preferredLanguage);
			}
			this.spinnerService.hide();
		},
		( error: any ) => {
			this.logoutProfile();
			this.spinnerService.hide();
		} );
	}

	logoutProfile() {
		this.spinnerService.show();
		const URL = environment.API_ENDPOINT + ROUTE_PREFIX.PUBLIC
		+ (this.profileType === USER_TYPE.BUYER ? ROUTE_PREFIX.BUYER : ROUTE_PREFIX.SUPPLIER) + API_ENDPOINTS.LOGOUT;
		this.publicHttpService.publicHttpClient.get( URL, {withCredentials: true} ).subscribe( () => {
			this.spinnerService.hide();
			this.sessionService.clear();
			window.stop();
			this.utilityService.navigate( ROUTES.LOG_IN );
		}, ( ) => {
			this.spinnerService.hide();
			this.sessionService.clear();
			window.stop();
			this.utilityService.navigate( ROUTES.LOG_IN );
		} );
	}

	logoutProfileWithoutRequest() {
		this.spinnerService.hide();
		this.sessionService.clear();
		window.stop();
		this.utilityService.navigate( ROUTES.LOG_IN );
	}

	updateUser( userDetails: Partial<IUserProfile> ): Observable<any> {
		const URI = this.profileType === USER_TYPE.BUYER ? environment.API_ROUTES.BUYER.PROFILE.GET : environment.API_ROUTES.SUPPLIER.PROFILE.PUT;
		return this.httpClient.put( environment.API_ENDPOINT + environment.VERSION_V1 + URI, userDetails, { observe: "response" } );
	}

	updatePassword( currentPassword: string, newPassword: string, confirmPassword: string ): Observable<any> {
		const URI = this.profileType === USER_TYPE.BUYER ? environment.API_ROUTES.BUYER.PROFILE.UPDATE_PASSWORD : environment.API_ROUTES.SUPPLIER.PROFILE.UPDATE_PASSWORD;
		const payload = {
			"currentPassword": currentPassword,
			"newPassword": newPassword,
			"confirmPassword": confirmPassword
		};
		return this.httpClient.put( environment.API_ENDPOINT + environment.VERSION_V1 + URI, payload, { observe: "response" } );
	}

	setLastSelectedOrganization( id: string ) {
		const URI = this.profileType === USER_TYPE.BUYER ? environment.API_ROUTES.BUYER.PROFILE.GET : environment.API_ROUTES.SUPPLIER.PROFILE.PUT;
		const payload = {
			"lastSelectedOrganizationId": id
		};
		this.httpClient.put( environment.API_ENDPOINT + environment.VERSION_V1 + URI, payload ).
			subscribe( ( ) => {
				this.loadProfile();
			} );
	}

	disableWelcomeScreen() {
		const URI = this.profileType === USER_TYPE.BUYER ? environment.API_ROUTES.BUYER.PROFILE.GET : environment.API_ROUTES.SUPPLIER.PROFILE.GET;
		const payload = {
			"showWelcomeScreen": "false"
		};
		this.httpClient.put( environment.API_ENDPOINT + environment.VERSION_V1 + URI, payload ).
			subscribe( ( ) => {
				this.loadProfile();
			} );
	}

	deleteProfile(): Observable<any> {
		const URI = this.profileType === USER_TYPE.BUYER ? environment.API_ROUTES.BUYER.PROFILE.GET : environment.API_ROUTES.SUPPLIER.PROFILE.PUT;
		return this.httpClient.delete( environment.API_ENDPOINT + environment.VERSION_V1 + URI );
	}

	getProfileName$() {
		return this._profileName$.asObservable();
	}

	setProfileName( profile: IUserProfile ) {
		if( profile ){
			if( profile.firstName || profile.lastName ){
				if( !profile.lastName ) {
					this._profileName$.next( profile.firstName );
				} else {
					this._profileName$.next( profile.firstName+" "+profile.lastName );
				}
			} else {
				this._profileName$.next( profile.email );
			}
		}
	}

	getCurrentOrganization(): string {
		return this.sessionService.currentOrgId;
	}

	/**
	 * Handle update org functionality
	 * @param payload : organization partial
	 * @returns
	 */
	updateOrganization( payload: Partial<IOrganization> , notification? ) {
		this.spinnerService.show();
		const url = environment.API_ENDPOINT + environment.VERSION_V1 + this._userRoutes.ORGANISATION.BASE_URL+"/"+payload.id;
		return this.httpClient.put( url, payload );
	}
	updateMyOrganization(orgId, payload, notification? ) {
		this.spinnerService.show();
		const url = environment.API_ENDPOINT + environment.VERSION_V1 + this._userRoutes.ORGANISATION.BASE_URL+"/"+orgId;
		return this.httpClient.put( url, payload );
	}
	

	checkIfCategoriesAreNeeded(): Observable<Boolean> {
		return new Observable<Boolean>(observer => {
			this.lastSelectedOrganization$.subscribe(org => {
				if (org.id && !org.initialCategoriesSelected) {
					observer.next(true);
				}
			});
		});
	}

	deactivateUser(organisationId: string,userId: string) {
		const url = this.profileType === 'buyer' ?
			environment.API_ENDPOINT + environment.VERSION_V1 + environment.API_ROUTES.BUYER.ORGANISATION.BASE_URL + `/${organisationId}`  +environment.API_ROUTES.BUYER.ORGANISATION.USERS.BASE_URL + `/${userId}` + environment.API_ROUTES.BUYER.ORGANISATION.USERS.DEACTIVATE :
			environment.API_ENDPOINT + environment.VERSION_V1 + environment.API_ROUTES.SUPPLIER.ORGANISATION.BASE_URL + `/${organisationId}`  +environment.API_ROUTES.SUPPLIER.ORGANISATION.USERS.BASE_URL + `/${userId}` + environment.API_ROUTES.SUPPLIER.ORGANISATION.USERS.DEACTIVATE ;
		return this.httpClient.put(url,{});
	}

	activateUser(organisationId: string,userId: string) {
		const url = this.profileType === 'buyer' ?
			environment.API_ENDPOINT + environment.VERSION_V1 + environment.API_ROUTES.BUYER.ORGANISATION.BASE_URL + `/${organisationId}`  +environment.API_ROUTES.BUYER.ORGANISATION.USERS.BASE_URL + `/${userId}` + environment.API_ROUTES.BUYER.ORGANISATION.USERS.ACTIVATE :
			environment.API_ENDPOINT + environment.VERSION_V1 + environment.API_ROUTES.SUPPLIER.ORGANISATION.BASE_URL + `/${organisationId}`  +environment.API_ROUTES.SUPPLIER.ORGANISATION.USERS.BASE_URL + `/${userId}` + environment.API_ROUTES.SUPPLIER.ORGANISATION.USERS.ACTIVATE ;
		return this.httpClient.put(url,{});
	}
}
